/**
 * T3 Code current-main attachment classification, paste, and whole-column drop behavior adapted
 * from MIT-licensed revision 053affbed2659f90cd1b1efaaa7a75865c4131c7. PapersFlow keeps bounded
 * text/code in message context; this module does not upload or persist file bytes.
 */

export const MAX_COMPOSER_CONTEXT_FILES = 4;
export const MAX_COMPOSER_CONTEXT_FILE_BYTES = 256 * 1024;
export const MAX_COMPOSER_CONTEXT_FILE_CHARACTERS = 8_000;
export const MAX_COMPOSER_CONTEXT_TOTAL_CHARACTERS = 24_000;
export const MAX_RETAINED_COMPOSER_CONTEXT_REJECTIONS = 3;
export const PROTECTED_COMPOSER_CONTEXT_PATH_MESSAGE =
  "Protected secret, credential, or key paths cannot be added to Doxa context.";

const GENERIC_MEDIA_TYPES = new Set(["", "application/octet-stream"]);
const UNSUPPORTED_FILE_EXTENSIONS = new Set([
  "7z",
  "avif",
  "bin",
  "bmp",
  "bz2",
  "dll",
  "dmg",
  "exe",
  "gif",
  "gz",
  "heic",
  "heif",
  "ico",
  "jpeg",
  "jpg",
  "pdf",
  "png",
  "rar",
  "svg",
  "tar",
  "tif",
  "tiff",
  "webp",
  "xz",
  "zip",
]);
const UNSUPPORTED_MEDIA_TYPES = new Set([
  "application/gzip",
  "application/pdf",
  "application/vnd.rar",
  "application/x-7z-compressed",
  "application/x-rar-compressed",
  "application/x-tar",
  "application/zip",
]);
const SUPPORTED_APPLICATION_MEDIA_TYPES = new Set([
  "application/ecmascript",
  "application/graphql",
  "application/javascript",
  "application/json",
  "application/json5",
  "application/sql",
  "application/toml",
  "application/typescript",
  "application/x-httpd-php",
  "application/x-javascript",
  "application/x-ndjson",
  "application/x-sh",
  "application/x-sql",
  "application/x-toml",
  "application/x-typescript",
  "application/x-yaml",
  "application/xhtml+xml",
  "application/xml",
  "application/yaml",
]);
const PROTECTED_COMPOSER_CONTEXT_DIRECTORY_NAMES = new Set([
  ".aws",
  ".azure",
  ".cache",
  ".claude",
  ".codex",
  ".cursor",
  ".docker",
  ".gnupg",
  ".kube",
  ".opencode",
  ".password-store",
  ".ssh",
  "keyrings",
]);
const PROTECTED_COMPOSER_CONTEXT_CONFIGURATION_DIRECTORIES = new Set([
  "1password",
  "cloudflared",
  "gcloud",
  "gh",
  "hub",
  "op",
  "rclone",
]);
const PROTECTED_COMPOSER_CONTEXT_FILE_NAMES = new Set([
  ".git-credentials",
  ".netrc",
  ".npmrc",
  ".pnpmrc",
  ".s3cfg",
  ".yarnrc",
  ".yarnrc.yml",
  "access_tokens.db",
  "auth.db",
  "auth.json",
  "auth.sqlite",
  "bunfig.toml",
  "credentials",
  "credentials.db",
  "credentials.json",
  "credentials.sqlite",
  "id_dsa",
  "id_ecdsa",
  "id_ed25519",
  "id_rsa",
  "secrets.json",
  "secrets.yaml",
  "secrets.yml",
  "token.json",
  "tokens.json",
]);
const PROTECTED_COMPOSER_CONTEXT_KEY_SUFFIXES = [
  ".jks",
  ".key",
  ".keystore",
  ".p12",
  ".p8",
  ".pem",
  ".pfx",
];

const MEDIA_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  astro: "text/x-astro",
  bash: "text/x-shellscript",
  bat: "text/plain",
  c: "text/x-c",
  cc: "text/x-c++",
  cfg: "text/plain",
  cjs: "text/javascript",
  clj: "text/x-clojure",
  cljs: "text/x-clojure",
  cmd: "text/plain",
  conf: "text/plain",
  cpp: "text/x-c++",
  cs: "text/x-csharp",
  css: "text/css",
  csv: "text/csv",
  cts: "text/typescript",
  cxx: "text/x-c++",
  diff: "text/x-diff",
  fish: "text/x-shellscript",
  go: "text/x-go",
  gql: "application/graphql",
  graphql: "application/graphql",
  h: "text/x-c",
  hpp: "text/x-c++",
  htm: "text/html",
  html: "text/html",
  ini: "text/plain",
  ipynb: "application/json",
  java: "text/x-java-source",
  jl: "text/x-julia",
  js: "text/javascript",
  json: "application/json",
  json5: "application/json5",
  jsonc: "application/json",
  jsonl: "application/x-ndjson",
  jsx: "text/jsx",
  kt: "text/x-kotlin",
  kts: "text/x-kotlin",
  less: "text/css",
  lock: "text/plain",
  log: "text/plain",
  lua: "text/x-lua",
  markdown: "text/markdown",
  md: "text/markdown",
  mdx: "text/markdown",
  mjs: "text/javascript",
  mts: "text/typescript",
  ndjson: "application/x-ndjson",
  php: "application/x-httpd-php",
  plist: "application/xml",
  proto: "text/x-protobuf",
  ps1: "text/plain",
  py: "text/x-python",
  r: "text/x-r",
  rb: "text/x-ruby",
  rs: "text/x-rust",
  rst: "text/plain",
  sass: "text/css",
  scala: "text/x-scala",
  scss: "text/css",
  sh: "text/x-shellscript",
  sql: "application/sql",
  svelte: "text/x-svelte",
  swift: "text/x-swift",
  tex: "text/x-tex",
  toml: "application/toml",
  ts: "text/typescript",
  tsv: "text/tab-separated-values",
  tsx: "text/tsx",
  txt: "text/plain",
  vue: "text/x-vue",
  xml: "application/xml",
  yaml: "application/yaml",
  yml: "application/yaml",
  zsh: "text/x-shellscript",
};

const MEDIA_TYPE_BY_EXACT_FILENAME: Readonly<Record<string, string>> = {
  ".dockerignore": "text/plain",
  ".editorconfig": "text/plain",
  ".gitattributes": "text/plain",
  ".gitignore": "text/plain",
  dockerfile: "text/x-dockerfile",
  makefile: "text/x-makefile",
};

const CONTEXT_FILE_PART_VERSION = "PAPERSFLOW_CONTEXT_FILE_PART_V1";
const UNTRUSTED_CONTEXT_LABEL = "[UNTRUSTED USER-SELECTED TEXT/CODE CONTEXT]";

export interface ComposerContextFile {
  readonly id: string;
  readonly filename: string;
  readonly mediaType: string;
  readonly sizeBytes: number;
  readonly text: string;
  readonly truncated: boolean;
}

export type ComposerContextFileRejectionCode =
  | "binary"
  | "empty"
  | "protected"
  | "read-failed"
  | "too-large"
  | "too-many"
  | "total-character-limit"
  | "unsupported";

export interface ComposerContextFileRejection {
  readonly filename: string;
  readonly code: ComposerContextFileRejectionCode;
  readonly message: string;
}

export type ComposerContextObject =
  | {
      readonly state: "pending";
      readonly id: string;
      readonly batchId: number;
      readonly filename: string;
      readonly sizeBytes: number;
    }
  | ({
      readonly state: "accepted";
      readonly batchId: number;
    } & ComposerContextFile)
  | ({
      readonly state: "rejected";
      readonly id: string;
      readonly batchId: number;
    } & ComposerContextFileRejection);

export interface ReadComposerContextFilesOptions {
  readonly createId: (file: File, inputIndex: number) => string;
  readonly existingFiles?: ReadonlyArray<Pick<ComposerContextFile, "text">>;
}

export interface ReadComposerContextFilesResult {
  readonly accepted: ReadonlyArray<ComposerContextFile>;
  readonly rejected: ReadonlyArray<ComposerContextFileRejection>;
}

export type ParsedContextFilePart = Omit<ComposerContextFile, "id">;

interface ReadContextFile {
  readonly kind: "read";
  readonly file: File;
  readonly inputIndex: number;
  readonly mediaType: string;
  readonly text: string;
}

interface RejectedContextFile {
  readonly kind: "rejected";
  readonly rejection: ComposerContextFileRejection;
}

type ContextFileReadResult = ReadContextFile | RejectedContextFile;

type ComposerContextFileCandidate = Pick<File, "name" | "size" | "type"> &
  Partial<Pick<File, "webkitRelativePath">>;

function rejection(
  file: Pick<File, "name">,
  code: ComposerContextFileRejectionCode,
  message: string,
): RejectedContextFile {
  return {
    kind: "rejected",
    rejection: { filename: file.name, code, message },
  };
}

function normalizedMediaType(mediaType: string): string {
  return mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
}

function filenameExtension(filename: string): string | null {
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === filename.length - 1) return null;
  return filename.slice(dotIndex + 1);
}

export function isProtectedComposerContextPath(value: string): boolean {
  const components = value.split("/").map((component) => component.toLowerCase());
  const basename = components.at(-1) ?? "";
  if (
    components.some(
      (component) =>
        component === ".env" || component.startsWith(".env.") || component.endsWith(".env"),
    )
  ) {
    return true;
  }
  if (components.some((component) => PROTECTED_COMPOSER_CONTEXT_DIRECTORY_NAMES.has(component))) {
    return true;
  }
  if (
    components.some(
      (component, index) =>
        component === ".config" &&
        PROTECTED_COMPOSER_CONTEXT_CONFIGURATION_DIRECTORIES.has(components[index + 1] ?? ""),
    )
  ) {
    return true;
  }
  if (
    components.some(
      (component, index) =>
        component === ".git" &&
        ((components[index + 1] ?? "") === "config" ||
          (components[index + 1] ?? "").startsWith("credential")),
    )
  ) {
    return true;
  }
  return (
    PROTECTED_COMPOSER_CONTEXT_FILE_NAMES.has(basename) ||
    PROTECTED_COMPOSER_CONTEXT_KEY_SUFFIXES.some((suffix) => basename.endsWith(suffix))
  );
}

function isProtectedComposerContextFile(file: ComposerContextFileCandidate): boolean {
  const relativePath = file.webkitRelativePath;
  return (
    isProtectedComposerContextPath(file.name) ||
    (relativePath !== undefined &&
      relativePath.length > 0 &&
      isProtectedComposerContextPath(relativePath))
  );
}

function inferredMediaType(filename: string): string | null {
  const lowerFilename = filename.toLowerCase();
  const exactMediaType = MEDIA_TYPE_BY_EXACT_FILENAME[lowerFilename];
  if (exactMediaType !== undefined) {
    return exactMediaType;
  }
  const extension = filenameExtension(lowerFilename);
  return extension === null ? null : (MEDIA_TYPE_BY_EXTENSION[extension] ?? null);
}

function isSupportedMediaType(mediaType: string): boolean {
  return (
    mediaType.startsWith("text/") ||
    mediaType.endsWith("+json") ||
    mediaType.endsWith("+xml") ||
    SUPPORTED_APPLICATION_MEDIA_TYPES.has(mediaType)
  );
}

function isExplicitlyUnsupportedMediaType(mediaType: string): boolean {
  return (
    mediaType.startsWith("audio/") ||
    mediaType.startsWith("font/") ||
    mediaType.startsWith("image/") ||
    mediaType.startsWith("video/") ||
    UNSUPPORTED_MEDIA_TYPES.has(mediaType)
  );
}

export function inferComposerContextFileMediaType(
  file: Pick<File, "name" | "type">,
): string | null {
  const extension = filenameExtension(file.name.toLowerCase());
  if (extension !== null && UNSUPPORTED_FILE_EXTENSIONS.has(extension)) return null;
  const browserMediaType = normalizedMediaType(file.type);
  if (isExplicitlyUnsupportedMediaType(browserMediaType)) return null;
  if (GENERIC_MEDIA_TYPES.has(browserMediaType)) {
    return inferredMediaType(file.name);
  }
  return isSupportedMediaType(browserMediaType) ? browserMediaType : null;
}

function inspectComposerContextFileCandidate(
  file: ComposerContextFileCandidate,
):
  | { readonly accepted: true; readonly mediaType: string }
  | { readonly accepted: false; readonly rejection: ComposerContextFileRejection } {
  if (isProtectedComposerContextFile(file)) {
    return {
      accepted: false,
      rejection: {
        filename: file.name,
        code: "protected",
        message: PROTECTED_COMPOSER_CONTEXT_PATH_MESSAGE,
      },
    };
  }
  if (file.size === 0) {
    return {
      accepted: false,
      rejection: { filename: file.name, code: "empty", message: `'${file.name}' is empty.` },
    };
  }
  if (file.size > MAX_COMPOSER_CONTEXT_FILE_BYTES) {
    return {
      accepted: false,
      rejection: {
        filename: file.name,
        code: "too-large",
        message: `'${file.name}' exceeds the 256 KiB context-file limit.`,
      },
    };
  }
  const mediaType = inferComposerContextFileMediaType(file);
  if (mediaType === null) {
    return {
      accepted: false,
      rejection: {
        filename: file.name,
        code: "unsupported",
        message: `'${file.name}' is not a supported text, code, or data file.`,
      },
    };
  }
  return { accepted: true, mediaType };
}

export function validateComposerContextFileCandidate(
  file: ComposerContextFileCandidate,
): ComposerContextFileRejection | null {
  const candidate = inspectComposerContextFileCandidate(file);
  return candidate.accepted ? null : candidate.rejection;
}

export function retainBoundedComposerContextRejections(
  objects: readonly ComposerContextObject[],
): readonly ComposerContextObject[] {
  const protectedObjects = objects.filter((object) => object.state !== "rejected");
  const rejections = objects.filter((object) => object.state === "rejected");
  const availableRejectionSlots = Math.min(
    MAX_RETAINED_COMPOSER_CONTEXT_REJECTIONS,
    Math.max(0, MAX_COMPOSER_CONTEXT_FILES - protectedObjects.length),
  );
  if (rejections.length <= availableRejectionSlots) return objects;
  if (availableRejectionSlots === 0) return protectedObjects;
  const retainedIds = new Set(
    rejections.slice(-availableRejectionSlots).map((object) => object.id),
  );
  return objects.filter((object) => object.state !== "rejected" || retainedIds.has(object.id));
}

export interface ComposerContextBatch {
  readonly batchId: number;
  readonly generation: number;
  readonly filesToRead: readonly File[];
  readonly immediateRejections: readonly ComposerContextObject[];
  readonly pendingObjects: readonly ComposerContextObject[];
}

export interface ComposerContextBatchCompletion {
  readonly applied: boolean;
  readonly objects: readonly ComposerContextObject[];
}

export type ComposerContextEnqueueResult =
  | {
      readonly status: "completed";
      readonly accepted: ReadonlyArray<ComposerContextFile>;
      readonly rejected: ReadonlyArray<ComposerContextFileRejection>;
      readonly objects: readonly ComposerContextObject[];
    }
  | {
      readonly status: "stale";
      readonly objects: readonly ComposerContextObject[];
    };

export interface ComposerContextEnqueueOptions {
  readonly isCurrent?: () => boolean;
  readonly onObjectsChanged?: (objects: readonly ComposerContextObject[]) => void;
}

interface ComposerContextEnqueueHost {
  readonly complete: (
    batch: ComposerContextBatch,
    result: ReadComposerContextFilesResult,
  ) => ComposerContextBatchCompletion;
  readonly discard: (batch: ComposerContextBatch) => ComposerContextBatchCompletion;
  readonly getObjects: () => readonly ComposerContextObject[];
  readonly stage: (files: readonly File[]) => ComposerContextBatch;
}

interface ComposerContextReadQueue {
  current: Promise<void>;
}

function staleEnqueueResult(
  batch: ComposerContextBatch,
  host: ComposerContextEnqueueHost,
  options: ComposerContextEnqueueOptions,
): ComposerContextEnqueueResult {
  const discarded = host.discard(batch);
  if (discarded.applied) options.onObjectsChanged?.(discarded.objects);
  return { status: "stale", objects: host.getObjects() };
}

function enqueueComposerContextFiles(
  files: readonly File[],
  options: ComposerContextEnqueueOptions,
  queue: ComposerContextReadQueue,
  host: ComposerContextEnqueueHost,
): Promise<ComposerContextEnqueueResult> {
  const isCurrent = options.isCurrent ?? (() => true);
  if (!isCurrent()) {
    return Promise.resolve({ status: "stale", objects: host.getObjects() });
  }

  const batch = host.stage(files);
  options.onObjectsChanged?.(host.getObjects());
  if (!isCurrent()) return Promise.resolve(staleEnqueueResult(batch, host, options));

  const immediateRejections = batch.immediateRejections.flatMap((object) =>
    object.state === "rejected"
      ? [{ filename: object.filename, code: object.code, message: object.message }]
      : [],
  );
  if (batch.filesToRead.length === 0) {
    return Promise.resolve({
      status: "completed",
      accepted: [],
      rejected: immediateRejections,
      objects: host.getObjects(),
    });
  }

  const readNext = async (): Promise<ComposerContextEnqueueResult> => {
    if (!isCurrent()) return staleEnqueueResult(batch, host, options);
    const existingFiles = host
      .getObjects()
      .flatMap((object) => (object.state === "accepted" ? [object] : []));
    let result: ReadComposerContextFilesResult;
    try {
      result = await readComposerContextFiles(batch.filesToRead, {
        createId: (_file, inputIndex) => {
          const pendingObject = batch.pendingObjects[inputIndex];
          if (pendingObject === undefined) {
            throw new RangeError("Context-file result did not match its pending input.");
          }
          return pendingObject.id;
        },
        existingFiles,
      });
    } catch {
      result = {
        accepted: [],
        rejected: batch.pendingObjects.map((pending) => ({
          filename: pending.filename,
          code: "read-failed",
          message: `'${pending.filename}' could not be read as text.`,
        })),
      };
    }

    if (!isCurrent()) return staleEnqueueResult(batch, host, options);
    await Promise.resolve();
    if (!isCurrent()) return staleEnqueueResult(batch, host, options);
    const completion = host.complete(batch, result);
    if (!completion.applied) {
      return { status: "stale", objects: host.getObjects() };
    }
    options.onObjectsChanged?.(completion.objects);
    return {
      status: "completed",
      accepted: result.accepted,
      rejected: [...immediateRejections, ...result.rejected],
      objects: completion.objects,
    };
  };

  const queuedRead = queue.current.then(readNext, readNext);
  queue.current = queuedRead.then(
    () => undefined,
    () => undefined,
  );
  return queuedRead;
}

export function createComposerContextCoordinator(createId: () => string) {
  let objects: readonly ComposerContextObject[] = [];
  let generation = 0;
  let nextBatchId = 0;
  const readQueue: ComposerContextReadQueue = { current: Promise.resolve() };

  function stage(files: readonly File[]): ComposerContextBatch {
    const batchId = (nextBatchId += 1);
    const reservedSlots = objects.filter(
      (object) => object.state === "accepted" || object.state === "pending",
    ).length;
    const availableSlots = Math.max(0, MAX_COMPOSER_CONTEXT_FILES - reservedSlots);
    const filesToRead: File[] = [];
    const immediateRejections: ComposerContextObject[] = [];

    for (const file of files) {
      const candidateRejection = validateComposerContextFileCandidate(file);
      if (candidateRejection !== null) {
        immediateRejections.push({
          state: "rejected",
          id: createId(),
          batchId,
          ...candidateRejection,
        });
        continue;
      }
      if (filesToRead.length >= availableSlots) {
        immediateRejections.push({
          state: "rejected",
          id: createId(),
          batchId,
          filename: file.name,
          code: "too-many",
          message: `'${file.name}' was not added because the composer accepts at most 4 context files.`,
        });
        continue;
      }
      filesToRead.push(file);
    }

    const pendingObjects = filesToRead.map((file): ComposerContextObject => ({
      state: "pending",
      id: createId(),
      batchId,
      filename: file.name || "Untitled file",
      sizeBytes: file.size,
    }));
    objects = retainBoundedComposerContextRejections([
      ...objects,
      ...pendingObjects,
      ...immediateRejections,
    ]);
    return {
      batchId,
      generation,
      filesToRead,
      immediateRejections,
      pendingObjects,
    };
  }

  function complete(
    batch: ComposerContextBatch,
    result: ReadComposerContextFilesResult,
  ): ComposerContextBatchCompletion {
    if (batch.generation !== generation) return { applied: false, objects };
    const pendingIds = new Set(batch.pendingObjects.map((object) => object.id));
    const insertionIndex = objects.findIndex((object) => pendingIds.has(object.id));
    if (insertionIndex < 0) return { applied: false, objects };
    const resolvedObjects: ComposerContextObject[] = [
      ...result.accepted.map((file): ComposerContextObject => ({
        state: "accepted",
        batchId: batch.batchId,
        ...file,
      })),
      ...result.rejected.map((file): ComposerContextObject => ({
        state: "rejected",
        id: createId(),
        batchId: batch.batchId,
        ...file,
      })),
    ];
    const remainingObjects = objects.filter((object) => !pendingIds.has(object.id));
    objects = retainBoundedComposerContextRejections([
      ...remainingObjects.slice(0, insertionIndex),
      ...resolvedObjects,
      ...remainingObjects.slice(insertionIndex),
    ]);
    return { applied: true, objects };
  }

  function fail(batch: ComposerContextBatch): ComposerContextBatchCompletion {
    return complete(batch, {
      accepted: [],
      rejected: batch.pendingObjects.map((pending) => ({
        filename: pending.filename,
        code: "read-failed",
        message: `'${pending.filename}' could not be read as text.`,
      })),
    });
  }

  function discard(batch: ComposerContextBatch): ComposerContextBatchCompletion {
    if (batch.generation !== generation) return { applied: false, objects };
    const nextObjects = objects.filter((object) => object.batchId !== batch.batchId);
    if (nextObjects.length === objects.length) return { applied: false, objects };
    objects = nextObjects;
    return { applied: true, objects };
  }

  function enqueue(
    files: readonly File[],
    options: ComposerContextEnqueueOptions = {},
  ): Promise<ComposerContextEnqueueResult> {
    return enqueueComposerContextFiles(files, options, readQueue, {
      complete,
      discard,
      getObjects: () => objects,
      stage,
    });
  }

  function remove(id: string): readonly ComposerContextObject[] {
    const object = objects.find((candidate) => candidate.id === id);
    if (object?.state === "pending") return objects;
    objects = objects.filter((candidate) => candidate.id !== id);
    return objects;
  }

  function takeAccepted(): readonly ComposerContextFile[] {
    const accepted = objects.flatMap((object) => (object.state === "accepted" ? [object] : []));
    generation += 1;
    objects = [];
    return accepted;
  }

  return {
    complete,
    discard,
    enqueue,
    fail,
    getObjects: () => objects,
    remove,
    stage,
    takeAccepted,
  };
}

function containsDisallowedControlCharacters(text: string): boolean {
  for (const character of text) {
    const codePoint = character.codePointAt(0);
    if (
      codePoint !== undefined &&
      ((codePoint < 32 && codePoint !== 9 && codePoint !== 10 && codePoint !== 13) ||
        (codePoint >= 127 && codePoint <= 159))
    ) {
      return true;
    }
  }
  return false;
}

async function readContextFile(file: File, inputIndex: number): Promise<ContextFileReadResult> {
  const candidate = inspectComposerContextFileCandidate(file);
  if (!candidate.accepted) {
    return { kind: "rejected", rejection: candidate.rejection };
  }

  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    return rejection(file, "read-failed", `'${file.name}' could not be read as text.`);
  }

  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return rejection(
      file,
      "binary",
      `'${file.name}' contains binary or non-UTF-8 data and cannot be added as text context.`,
    );
  }
  if (containsDisallowedControlCharacters(text)) {
    return rejection(
      file,
      "binary",
      `'${file.name}' contains binary or non-UTF-8 data and cannot be added as text context.`,
    );
  }
  return { kind: "read", file, inputIndex, mediaType: candidate.mediaType, text };
}

export async function readComposerContextFiles(
  files: ReadonlyArray<File>,
  options: ReadComposerContextFilesOptions,
): Promise<ReadComposerContextFilesResult> {
  const existingFiles = options.existingFiles ?? [];
  let totalCharacters = existingFiles.reduce((total, file) => total + file.text.length, 0);
  const reads = await Promise.all(files.map((file, index) => readContextFile(file, index)));
  const accepted: ComposerContextFile[] = [];
  const rejected: ComposerContextFileRejection[] = [];

  for (const result of reads) {
    if (result.kind === "rejected") {
      rejected.push(result.rejection);
      continue;
    }
    if (result.text.length === 0) {
      rejected.push(rejection(result.file, "empty", `'${result.file.name}' is empty.`).rejection);
      continue;
    }
    if (existingFiles.length + accepted.length >= MAX_COMPOSER_CONTEXT_FILES) {
      rejected.push(
        rejection(
          result.file,
          "too-many",
          `'${result.file.name}' was not added because the composer accepts at most 4 context files.`,
        ).rejection,
      );
      continue;
    }

    const remainingCharacters = MAX_COMPOSER_CONTEXT_TOTAL_CHARACTERS - totalCharacters;
    if (remainingCharacters <= 0) {
      rejected.push(
        rejection(
          result.file,
          "total-character-limit",
          `'${result.file.name}' was not added because context text is limited to 24,000 characters.`,
        ).rejection,
      );
      continue;
    }

    const acceptedCharacters = Math.min(
      result.text.length,
      MAX_COMPOSER_CONTEXT_FILE_CHARACTERS,
      remainingCharacters,
    );
    const text = result.text.slice(0, acceptedCharacters);
    accepted.push({
      id: options.createId(result.file, result.inputIndex),
      filename: result.file.name,
      mediaType: result.mediaType,
      sizeBytes: result.file.size,
      text,
      truncated: text.length < result.text.length,
    });
    totalCharacters += text.length;
  }

  return { accepted, rejected };
}

/** T3-style compact size label for context-file rows. */
export function formatContextFileSize(sizeBytes: number): string {
  return sizeBytes >= 1024 * 1024
    ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.ceil(sizeBytes / 1024))} KB`;
}

/** Preserve an ordinary text paste when the clipboard also supplies generic files. */
export function shouldHandleComposerContextFilePaste(input: {
  readonly files: ReadonlyArray<File>;
  readonly plainText: string;
}): boolean {
  return input.plainText.length === 0 && input.files.length > 0;
}

export interface ComposerContextFileDragEvent {
  readonly dataTransfer: {
    readonly types: ReadonlyArray<string>;
    readonly files: Iterable<File>;
    dropEffect: string;
  };
  readonly relatedTarget: EventTarget | null;
  readonly currentTarget: {
    contains(target: Node | null): boolean;
  };
  preventDefault(): void;
}

export interface ComposerContextFileDropHost {
  setDragActive(active: boolean): void;
  addFiles(files: File[]): void;
}

function isFileDrag(event: ComposerContextFileDragEvent): boolean {
  return event.dataTransfer.types.includes("Files");
}

function isDomNode(target: EventTarget): target is Node {
  return typeof Node === "undefined" || target instanceof Node;
}

function movedWithinDropTarget(event: ComposerContextFileDragEvent): boolean {
  return (
    event.relatedTarget !== null &&
    isDomNode(event.relatedTarget) &&
    event.currentTarget.contains(event.relatedTarget)
  );
}

export function makeComposerContextFileDropHandlers(host: ComposerContextFileDropHost) {
  return {
    onDragEnter(event: ComposerContextFileDragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      if (movedWithinDropTarget(event)) {
        return;
      }
      host.setDragActive(true);
    },
    onDragOver(event: ComposerContextFileDragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      host.setDragActive(true);
    },
    onDragLeave(event: ComposerContextFileDragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      if (movedWithinDropTarget(event)) {
        return;
      }
      host.setDragActive(false);
    },
    onDrop(event: ComposerContextFileDragEvent) {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      host.setDragActive(false);
      host.addFiles(Array.from(event.dataTransfer.files));
    },
  };
}

export function serializeContextFilePart(file: ComposerContextFile): string {
  return [
    CONTEXT_FILE_PART_VERSION,
    UNTRUSTED_CONTEXT_LABEL,
    `filename=${encodeURIComponent(file.filename)}`,
    `mediaType=${encodeURIComponent(file.mediaType)}`,
    `sizeBytes=${file.sizeBytes}`,
    `truncated=${file.truncated}`,
    `textCharacters=${file.text.length}`,
    "",
    file.text,
  ].join("\n");
}

function encodedHeaderValue(line: string | undefined, prefix: string): string | null {
  if (line === undefined || !line.startsWith(prefix)) {
    return null;
  }
  try {
    return decodeURIComponent(line.slice(prefix.length));
  } catch {
    return null;
  }
}

function parseUnsignedInteger(line: string | undefined, prefix: string): number | null {
  if (line === undefined || !line.startsWith(prefix)) {
    return null;
  }
  const value = line.slice(prefix.length);
  if (!/^(?:0|[1-9]\d*)$/.test(value)) {
    return null;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function isValidParsedFilename(filename: string | null): filename is string {
  return filename !== null && filename.length > 0 && !containsDisallowedControlCharacters(filename);
}

function parsedMediaType(mediaTypeValue: string | null): string | null {
  if (mediaTypeValue === null) {
    return null;
  }
  const mediaType = normalizedMediaType(mediaTypeValue);
  return mediaType === mediaTypeValue && isSupportedMediaType(mediaType) ? mediaType : null;
}

function isValidParsedSize(sizeBytes: number | null): sizeBytes is number {
  return sizeBytes !== null && sizeBytes > 0 && sizeBytes <= MAX_COMPOSER_CONTEXT_FILE_BYTES;
}

function isValidTruncatedValue(value: string | null): value is "false" | "true" {
  return value === "true" || value === "false";
}

function hasValidParsedText(text: string, textCharacters: number | null): boolean {
  return (
    textCharacters !== null &&
    textCharacters > 0 &&
    textCharacters <= MAX_COMPOSER_CONTEXT_FILE_CHARACTERS &&
    text.length === textCharacters &&
    !containsDisallowedControlCharacters(text)
  );
}

export function parseContextFilePart(value: string): ParsedContextFilePart | null {
  const separatorIndex = value.indexOf("\n\n");
  if (separatorIndex < 0) {
    return null;
  }
  const header = value.slice(0, separatorIndex).split("\n");
  if (
    header.length !== 7 ||
    header[0] !== CONTEXT_FILE_PART_VERSION ||
    header[1] !== UNTRUSTED_CONTEXT_LABEL
  ) {
    return null;
  }

  const filename = encodedHeaderValue(header[2], "filename=");
  const mediaTypeValue = encodedHeaderValue(header[3], "mediaType=");
  const sizeBytes = parseUnsignedInteger(header[4], "sizeBytes=");
  const truncatedValue = header[5]?.startsWith("truncated=")
    ? header[5].slice("truncated=".length)
    : null;
  const textCharacters = parseUnsignedInteger(header[6], "textCharacters=");
  const text = value.slice(separatorIndex + 2);
  const mediaType = parsedMediaType(mediaTypeValue);

  if (!isValidParsedFilename(filename)) {
    return null;
  }
  if (mediaType === null) {
    return null;
  }
  if (!isValidParsedSize(sizeBytes)) {
    return null;
  }
  if (!isValidTruncatedValue(truncatedValue)) {
    return null;
  }
  if (!hasValidParsedText(text, textCharacters)) {
    return null;
  }

  return {
    filename,
    mediaType,
    sizeBytes,
    text,
    truncated: truncatedValue === "true",
  };
}
